import { Button, PageLoader, State } from '@/components/atoms';
import { DataTable } from '@/components/molecules';
import { usePickupLocations, useDeletePickupLocation } from '@/api';
import type { ColumnDef } from '@tanstack/react-table';
import type { PickupLocation } from '@/types';
import { Trash2, Pencil } from 'lucide-react';

interface AllPickupLocationsProps {
  onAdd: () => void;
  onEdit: (id: number) => void;
}

export const AllPickupLocations = ({
  onAdd,
  onEdit,
}: AllPickupLocationsProps) => {
  const { data: locations, isLoading } = usePickupLocations();
  const { mutate: remove } = useDeletePickupLocation();

  const columns: ColumnDef<PickupLocation>[] = [
    {
      accessorKey: 'name',
      header: 'Name',
      cell: ({ row }) => (
        <div className="font-medium">{row.getValue('name')}</div>
      ),
    },
    {
      accessorKey: 'addressLine1',
      header: 'Address',
      cell: ({ row }) => {
        const address =
          [
            row.original.addressLine1,
            row.original.city,
            row.original.state,
            row.original.country,
          ]
            .filter(Boolean)
            .join(', ') || '—';
        return (
          <div
            className="text-sm max-w-[220px] sm:max-w-[280px] truncate"
            title={address}
          >
            {address}
          </div>
        );
      },
    },
    {
      accessorKey: 'phoneNumber',
      header: 'Phone',
      cell: ({ row }) => (
        <div className="text-sm">{row.getValue('phoneNumber') ?? '—'}</div>
      ),
    },
    {
      accessorKey: 'createdAt',
      header: 'Date',
      cell: ({ row }) => (
        <div className="text-sm text-gray-500">
          {new Date(row.getValue('createdAt')).toLocaleDateString()}
        </div>
      ),
    },
    {
      id: 'actions',
      header: () => <span className="uppercase text-xs">Actions</span>,
      cell: ({ row }) => (
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              onEdit(row.original.id);
            }}
            className="text-xs"
          >
            <Pencil className="size-4" />
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              remove(row.original.id);
            }}
            className="text-xs text-red-500 hover:text-red-600"
          >
            <Trash2 className="size-4" />
          </Button>
        </div>
      ),
    },
  ];

  if (isLoading) {
    return <PageLoader message="Loading pickup locations..." />;
  }

  if (!locations || locations.length === 0) {
    return (
      <State
        description="You don't have any pickup locations yet"
        button={{
          icon: 'Plus',
          text: 'Add pickup location',
          onClick: onAdd,
        }}
      />
    );
  }

  return <DataTable columns={columns} data={locations} />;
};
